/** Project deadlines: turns dated obligations that live outside the Calendar
 *  (tender returns, interim valuation and payment dates, contract item
 *  responses, practical completion) into read-only all-day calendar markers.
 *  Pure and client-side: everything it reads is already hydrated in
 *  DataContext, so the calendar adds no round-trip and no schema. */

import type { Project, InterimApplication, ContractItem } from '../types/database';
import type { CalendarEvent, ProjectDeadlineKind, ProjectDeadlineMeta } from '../types/calendar';
import type { ExpiringWorkerCard } from '../types/workerCards';

export interface ProjectDeadline extends ProjectDeadlineMeta {
  id: string;
  date: Date;
  title: string;
}

interface BuildProjectDeadlinesInput {
  projects: Project[];
  applications: InterimApplication[];
  contractItems: ContractItem[];
  rangeStart: Date;
  rangeEnd: Date;
}

// Canonical and legacy stage ids both appear in stored rows, so both are listed.
const TENDER_ACTIVE_STAGES = new Set([
  'itt_received', 'enquiry_received',
  'tendering', 'tender_in_progress',
  'on_hold',
  'internal_review', 'client_review',
  'cleared_for_submission', 'ready_to_submit',
]);

const PRE_START_STAGES = new Set(['pre_start']);

const CONSTRUCTION_STAGES = new Set(['pre_start', 'on_site']);

// The defects period runs from practical completion to the end of the final
// account, so the release of the second half of retention can fall in any of these.
export const DEFECTS_PERIOD_STAGES = new Set(['practical_completion', 'defects_liability', 'final_account']);

// Payment milestones stop mattering once the money has landed or the
// application is dead; drafts have not started the payment clock yet.
const PAYMENT_CLOCK_STATUSES = new Set(['submitted', 'approved', 'certified', 'disputed']);

const CLOSED_CONTRACT_ITEM_STATUSES = new Set(['approved', 'closed', 'rejected']);

const CONTRACT_ITEM_LABEL: Record<string, string> = {
  rfi: 'RFI response',
  eot: 'EOT',
  variation: 'Variation',
  cvi: 'CVI',
  'payment-app': 'Payment application',
  retention: 'Retention',
};

export const DEADLINE_KIND_LABEL: Record<ProjectDeadlineKind, string> = {
  tender_return: 'Tender return',
  interim_valuation: 'Valuation date',
  payment_notice: 'Payment notice due',
  pay_less_notice: 'Pay less cut-off',
  final_payment: 'Final date for payment',
  start_on_site: 'Start on site',
  practical_completion: 'Practical completion',
  defects_liability_end: 'Defects period ends',
  contract_item: 'Response due',
  site_card_expiry: 'Site card expires',
};

/** Stored dates arrive as ISO strings, UK DD-MM-YYYY strings or hydrated Date
 *  objects depending on the path that wrote them. Returns local midnight so a
 *  marker never drifts onto the previous day in BST. */
export function parseDeadlineDate(raw: unknown): Date | null {
  if (!raw) return null;
  let parsed: Date | null = null;
  if (raw instanceof Date) {
    parsed = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  } else if (typeof raw === 'string') {
    const s = raw.trim();
    const uk = /^(\d{2})[-/](\d{2})[-/](\d{4})/.exec(s);
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (uk) parsed = new Date(Number(uk[3]), Number(uk[2]) - 1, Number(uk[1]));
    else if (iso) parsed = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  // A mistyped year (e.g. 0202) would otherwise plant a marker centuries away
  // or, worse, one that looks overdue forever.
  const year = parsed.getFullYear();
  if (year < 2000 || year > 2100) return null;
  return parsed;
}

function pick(record: object, ...keys: string[]): unknown {
  const r = record as Record<string, unknown>;
  for (const k of keys) {
    if (r[k] !== undefined && r[k] !== null && r[k] !== '') return r[k];
  }
  return undefined;
}

export function buildProjectDeadlines({
  projects,
  applications,
  contractItems,
  rangeStart,
  rangeEnd,
}: BuildProjectDeadlinesInput): ProjectDeadline[] {
  const from = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()).getTime();
  const to = rangeEnd.getTime();
  const out: ProjectDeadline[] = [];

  // Only projects the user can see and that are still live. An application or
  // RFI whose project is archived or out of scope is not the user's deadline.
  const liveProjects = new Map<string, Project>();
  for (const p of projects) {
    if (p.archiveStatus === 'archived' || p.archiveStatus === 'pending-deletion') continue;
    liveProjects.set(p.id, p);
  }

  const push = (
    id: string,
    kind: ProjectDeadlineKind,
    raw: unknown,
    project: Project,
    head: string,
    label: string,
    projectTab?: string,
  ) => {
    const date = parseDeadlineDate(raw);
    if (!date) return;
    const t = date.getTime();
    if (t < from || t > to) return;
    const projectName = project.name || 'Untitled project';
    out.push({
      id,
      kind,
      date,
      title: `${head} · ${projectName}`,
      label,
      projectId: project.id,
      projectName,
      projectTab,
    });
  };

  for (const p of liveProjects.values()) {
    const stage = String(pick(p, 'stage', 'projectStage', 'project_stage') ?? '');
    // tenderReturnDate is hydrated from tender_submission_date (the date it
    // actually went in), so it is not a fallback for the due date.
    if (TENDER_ACTIVE_STAGES.has(stage) && !pick(p, 'tenderSubmissionDate', 'tender_submission_date')) {
      const label = DEADLINE_KIND_LABEL.tender_return;
      push(`deadline-tender-${p.id}`, 'tender_return', pick(p, 'tenderDueDate', 'tender_due_date'), p, label, label, 'tendering');
    }
    // Once the job is on site the start date is history; only a project still
    // waiting to mobilise has a start that can be missed.
    if (PRE_START_STAGES.has(stage)) {
      const label = DEADLINE_KIND_LABEL.start_on_site;
      push(`deadline-start-${p.id}`, 'start_on_site', pick(p, 'startDate', 'start_date'), p, label, label);
    }
    if (CONSTRUCTION_STAGES.has(stage)) {
      const label = DEADLINE_KIND_LABEL.practical_completion;
      push(`deadline-pc-${p.id}`, 'practical_completion', pick(p, 'practicalCompletionDate', 'practical_completion_date'), p, label, label);
    }
    if (DEFECTS_PERIOD_STAGES.has(stage)) {
      const label = DEADLINE_KIND_LABEL.defects_liability_end;
      push(`deadline-dlp-${p.id}`, 'defects_liability_end', pick(p, 'defectsLiabilityEndDate', 'defects_liability_end_date'), p, label, label);
    }
  }

  for (const a of applications) {
    const project = liveProjects.get(String(pick(a, 'projectId', 'project_id') ?? ''));
    if (!project) continue;
    const n = pick(a, 'applicationNumber', 'application_number');
    const app = n !== undefined ? `App ${n}` : 'Application';
    const interim = (kind: ProjectDeadlineKind, id: string, raw: unknown) => {
      const label = DEADLINE_KIND_LABEL[kind];
      push(`deadline-${id}-${a.id}`, kind, raw, project, `${app} ${label.charAt(0).toLowerCase()}${label.slice(1)}`, label, 'interim');
    };
    const status = String(a.status ?? '');
    if (status === 'draft') {
      interim('interim_valuation', 'valuation', pick(a, 'periodEnd', 'period_end'));
      continue;
    }
    if (!PAYMENT_CLOCK_STATUSES.has(status)) continue;
    if (!pick(a, 'dateCertified', 'date_certified')) {
      interim('payment_notice', 'payment-notice', pick(a, 'dueDatePaymentNotice', 'due_date_payment_notice'));
    }
    interim('pay_less_notice', 'pay-less', pick(a, 'dueDatePayLess', 'due_date_pay_less'));
    if (!pick(a, 'datePaid', 'date_paid', 'paid_date')) {
      interim('final_payment', 'final-payment', pick(a, 'dueDateFinalPayment', 'due_date_final_payment'));
    }
  }

  for (const c of contractItems) {
    if (CLOSED_CONTRACT_ITEM_STATUSES.has(c.status)) continue;
    const project = liveProjects.get(String(pick(c, 'projectId', 'project_id') ?? ''));
    if (!project) continue;
    const label = `${CONTRACT_ITEM_LABEL[c.type] ?? 'Contract item'} due`;
    const ref = c.reference?.trim();
    // Only RFIs have a page of their own; EOTs, variations and CVIs are stored
    // as contract items but no project tab lists them, so they open the project.
    const tab = c.type === 'rfi' ? 'rfis' : undefined;
    push(`deadline-item-${c.id}`, 'contract_item', pick(c, 'dueDate', 'due_date'), project, ref ? `${ref} due` : label, label, tab);
  }

  return out.sort((x, y) => x.date.getTime() - y.date.getTime());
}

/** Workspace-level, so it sits beside buildProjectDeadlines rather than inside
 *  it. Cards already expired are left to the Employees banner: a marker on a
 *  past day reads as a deadline that was met. */
export function buildSiteCardDeadlines(
  cards: readonly ExpiringWorkerCard[],
  rangeStart: Date,
  rangeEnd: Date,
  today: Date = new Date(),
): ProjectDeadline[] {
  // Judged against today here, not the server's daysLeft snapshot, so a page
  // left open past midnight stops flagging a card that has since expired.
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const from = Math.max(
    startOfToday,
    new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()).getTime(),
  );
  const to = rangeEnd.getTime();
  const out: ProjectDeadline[] = [];
  for (const c of cards) {
    const date = parseDeadlineDate(c.expiryDate);
    if (!date) continue;
    const t = date.getTime();
    if (t < from || t > to) continue;
    const label = DEADLINE_KIND_LABEL.site_card_expiry;
    out.push({
      id: `deadline-sitecard-${c.id}`,
      kind: 'site_card_expiry',
      date,
      title: `${c.title || c.schemeCode} expires · ${c.holderName}`,
      label,
      projectId: '',
      projectName: c.holderName,
      holderType: c.holderType,
      holderId: c.holderId,
    });
  }
  return out;
}

/** Menu order for the visibility filter: the life of a job, tender to handover,
 *  with the payment cycle kept together. */
export const DEADLINE_KIND_ORDER: readonly ProjectDeadlineKind[] = [
  'tender_return',
  'interim_valuation',
  'payment_notice',
  'pay_less_notice',
  'final_payment',
  'contract_item',
  'start_on_site',
  'practical_completion',
  'defects_liability_end',
  'site_card_expiry',
];

/** Plural category names for the filter menu. DEADLINE_KIND_LABEL names one
 *  marker ("Response due"), which reads as nothing out of context. */
export const DEADLINE_KIND_FILTER_LABEL: Record<ProjectDeadlineKind, string> = {
  tender_return: 'Tender returns',
  interim_valuation: 'Valuation dates',
  payment_notice: 'Payment notices',
  pay_less_notice: 'Pay less cut-offs',
  final_payment: 'Final dates for payment',
  contract_item: 'RFI, EOT and variation responses',
  start_on_site: 'Starts on site',
  practical_completion: 'Practical completion',
  defects_liability_end: 'Defects period ends',
  site_card_expiry: 'Site card expiries',
};

const KNOWN_KINDS = new Set<string>(DEADLINE_KIND_ORDER);

/** The filter stores what is hidden, not what is shown, so a kind added later
 *  appears by default instead of silently staying off for everyone who ever
 *  touched the filter. Anything unrecognised (stale storage, a malformed
 *  bridge payload) is dropped, and the result is in canonical order. */
export function sanitizeHiddenDeadlineKinds(raw: unknown): ProjectDeadlineKind[] {
  if (!Array.isArray(raw)) return [];
  const wanted = new Set(raw.filter((k): k is string => typeof k === 'string' && KNOWN_KINDS.has(k)));
  return DEADLINE_KIND_ORDER.filter(k => wanted.has(k));
}

export function parseHiddenDeadlineKinds(stored: string | null): ProjectDeadlineKind[] {
  if (!stored) return [];
  try {
    return sanitizeHiddenDeadlineKinds(JSON.parse(stored));
  } catch {
    return [];
  }
}

export function toggleHiddenDeadlineKind(
  hidden: readonly ProjectDeadlineKind[],
  kind: ProjectDeadlineKind,
): ProjectDeadlineKind[] {
  const next = new Set(hidden);
  if (next.has(kind)) next.delete(kind);
  else next.add(kind);
  return DEADLINE_KIND_ORDER.filter(k => next.has(k));
}

/** Returns the input array itself when nothing is hidden, so memos downstream
 *  of an untouched filter see no change. */
export function filterProjectDeadlines<T extends { kind: ProjectDeadlineKind }>(
  deadlines: T[],
  hidden: readonly ProjectDeadlineKind[],
): T[] {
  if (hidden.length === 0) return deadlines;
  const off = new Set(hidden);
  return deadlines.filter(d => !off.has(d.kind));
}

/** Feed it the list from before filterProjectDeadlines: the menu count answers
 *  "how many would come back if I showed this", so a hidden kind must still
 *  report its real number. Every kind is present, zero or not, so the menu
 *  never has to guess at a missing key. */
export function countDeadlinesByKind(
  deadlines: readonly { kind: ProjectDeadlineKind }[],
): Record<ProjectDeadlineKind, number> {
  const counts = Object.fromEntries(DEADLINE_KIND_ORDER.map(k => [k, 0])) as Record<ProjectDeadlineKind, number>;
  for (const d of deadlines) {
    if (d.kind in counts) counts[d.kind] += 1;
  }
  return counts;
}

export function deadlineToCalendarEvent(d: ProjectDeadline, userId: string): CalendarEvent {
  const start = new Date(d.date);
  const end = new Date(d.date);
  end.setHours(23, 59, 59, 999);
  const stamp = start.toISOString();
  return {
    id: d.id,
    user_id: userId,
    title: d.title,
    description: `${d.label} for ${d.projectName}`,
    start_time: stamp,
    end_time: end.toISOString(),
    all_day: true,
    color: 'red',
    is_google_event: false,
    project_deadline: { kind: d.kind, label: d.label, projectId: d.projectId, projectName: d.projectName, projectTab: d.projectTab, holderType: d.holderType, holderId: d.holderId },
    created_at: stamp,
    updated_at: stamp,
  };
}
